'use client'

import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useCallback, useContext, useMemo } from 'react'

import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import store from '@/lib/configs/store-config'
import { LinkingColor, linkingColorsAtomFamily, LinkingTickerInfo } from '@/state/linking'
import {
  chartWidgetScopeKey,
  cleanupUnlinkedOptionRows,
  DEFAULT_GROUP_STATE,
  LinkingGroupState,
  linkingGroupStateAtomFamily,
  OptionChainOverride,
  optionRowsAtomFamily,
  optionRowsScopeKey,
  optionUnderlyingPriceAtomFamily,
  resolveLegacyChartOption,
  unlinkedCardStateAtomFamily,
} from '@/state/linking-group'
import { DEFAULT_CHART_ANALYTICS } from '@/state/linking-group'
import { WatchList } from '@/state/storage'
import { chartOptionAtomFamily } from '@/state/widget-instances'
import { ChartAnalyticsOption, ChartOption, PositionFilter } from '@/types/dashboard'

/**
 * Pure merge for a ChartOption patch onto a current value, including the
 * nested `analytics` sub-keys. Extracted so both the read-side hydration and
 * the write-side setter use identical merge semantics, and so the write side
 * can evaluate a functional update exactly once against a freshly-read atom
 * value instead of a value captured at render time.
 */
export function mergeChartOption(current: ChartOption, patch: ChartOption): ChartOption {
  return {
    ...current,
    ...patch,
    analytics: {
      ...DEFAULT_CHART_ANALYTICS,
      ...current.analytics,
      ...patch.analytics,
      vwap: {
        ...DEFAULT_CHART_ANALYTICS.vwap,
        ...current.analytics?.vwap,
        ...patch.analytics?.vwap,
      },
      twap: {
        ...DEFAULT_CHART_ANALYTICS.twap,
        ...current.analytics?.twap,
        ...patch.analytics?.twap,
      },
      deltaPercentage: {
        ...DEFAULT_CHART_ANALYTICS.deltaPercentage,
        ...current.analytics?.deltaPercentage,
        ...patch.analytics?.deltaPercentage,
      },
      netBuyVolumeBase: {
        ...DEFAULT_CHART_ANALYTICS.netBuyVolumeBase,
        ...current.analytics?.netBuyVolumeBase,
        ...patch.analytics?.netBuyVolumeBase,
      },
      parkinsonVolatility: {
        ...DEFAULT_CHART_ANALYTICS.parkinsonVolatility,
        ...current.analytics?.parkinsonVolatility,
        ...patch.analytics?.parkinsonVolatility,
      },
      totalLiqBase: {
        ...DEFAULT_CHART_ANALYTICS.totalLiqBase,
        ...current.analytics?.totalLiqBase,
        ...patch.analytics?.totalLiqBase,
      },
    },
  }
}

function isSameTickerInfo(a: LinkingTickerInfo | null, b: LinkingTickerInfo | null) {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.symbol === b.symbol &&
    a.type === b.type &&
    a.baseCurrency === b.baseCurrency &&
    a.expiry === b.expiry
  )
}

function getFocusedTabIdFromIndex(watchList: WatchList): string | null {
  if (watchList.selectedIndex < 0 || watchList.selectedIndex >= watchList.list.length) return null
  return watchList.list[watchList.selectedIndex]?.id ?? null
}

export function normalizeWatchListFocus(watchList: WatchList): WatchList {
  const focusedTabId = watchList.focusedTabId ?? getFocusedTabIdFromIndex(watchList)
  const focusedIndexFromId = focusedTabId
    ? watchList.list.findIndex((item) => item.id === focusedTabId)
    : -1
  const focusedIndex =
    focusedIndexFromId >= 0
      ? focusedIndexFromId
      : watchList.selectedIndex >= 0 && watchList.selectedIndex < watchList.list.length
        ? watchList.selectedIndex
        : -1
  const selectedIndex = focusedIndex >= 0 ? focusedIndex : -1
  const nextFocusedTabId = selectedIndex >= 0 ? watchList.list[selectedIndex].id : null

  if (watchList.focusedTabId === nextFocusedTabId && watchList.selectedIndex === selectedIndex) {
    return watchList
  }

  return {
    ...watchList,
    focusedTabId: nextFocusedTabId,
    selectedIndex,
  }
}

function normalizeWatchListUpdate(current: WatchList, nextInput: WatchList): WatchList {
  if (nextInput.focusedTabId !== current.focusedTabId) {
    return normalizeWatchListFocus(nextInput)
  }

  if (nextInput.selectedIndex !== current.selectedIndex) {
    return normalizeWatchListFocus({
      ...nextInput,
      focusedTabId: getFocusedTabIdFromIndex(nextInput),
    })
  }

  return normalizeWatchListFocus(nextInput)
}

function areWatchListsEqual(a: WatchList, b: WatchList) {
  if (a === b) return true
  if (a.selectedIndex !== b.selectedIndex || a.focusedTabId !== b.focusedTabId) return false
  if (a.list.length !== b.list.length) return false

  for (let i = 0; i < a.list.length; i++) {
    const left = a.list[i]
    const right = b.list[i]
    const keys = new Set([...Object.keys(left), ...Object.keys(right)])
    for (const key of keys) {
      if ((left as Record<string, unknown>)[key] !== (right as Record<string, unknown>)[key]) {
        return false
      }
    }
    if (left !== right && keys.size === 0) {
      return false
    }
  }

  return true
}

const POSITION_FILTER_KEYS = [
  'instruments',
  'expiries',
  'assets',
  'accounts',
  'size',
  'pnl',
  'strikeRange',
  'positionDelta',
] as const satisfies readonly (keyof PositionFilter)[]

/**
 * Fill in every PositionFilter sub-field a slice persisted before that field existed
 * may be missing. Single source of truth for useLinkingGroupState and
 * useLinkingGroupPositionFilter, so both hand out identical values — reading
 * `positionFilter` raw off the slice is NOT equivalent to either.
 */
export function normalizePositionFilter(raw: PositionFilter | undefined): PositionFilter {
  const defaults = DEFAULT_GROUP_STATE.positionFilter
  return {
    instruments: raw?.instruments ?? defaults.instruments,
    expiries: raw?.expiries ?? defaults.expiries,
    assets: raw?.assets ?? defaults.assets,
    accounts: raw?.accounts ?? defaults.accounts,
    size: raw?.size ?? defaults.size,
    pnl: raw?.pnl ?? defaults.pnl,
    strikeRange: raw?.strikeRange ?? defaults.strikeRange,
    positionDelta: raw?.positionDelta ?? defaults.positionDelta,
  }
}

/**
 * Sub-field reference equality. `normalizePositionFilter` allocates a fresh wrapper on
 * every call, so without this a selectAtom over it would report a change on every write
 * to ANY part of the slice — exactly what the narrow hook exists to avoid.
 */
export function isSamePositionFilter(a: PositionFilter, b: PositionFilter) {
  return a === b || POSITION_FILTER_KEYS.every((key) => a[key] === b[key])
}

function isSameOptionChainOverride(a: OptionChainOverride | null, b: OptionChainOverride | null) {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.sourceCardId === b.sourceCardId &&
    a.symbol === b.symbol &&
    a.type === b.type &&
    a.exchange === b.exchange &&
    a.baseCurrency === b.baseCurrency &&
    a.expiry === b.expiry &&
    a.pricePrecision === b.pricePrecision
  )
}

// =============================================================================
// HOW TO ADD NEW COMPONENTS THAT CONSUME LINKING GROUP STATE
// =============================================================================
//
// 1. COMPONENT THAT READS & WRITES STATE (e.g., WatchList, PositionTable)
//    -----------------------------------------------------------------------
//    Use useLinkingGroupState(cardId) to get full read/write access:
//
//    ```tsx
//    export default function MyComponent() {
//      const cardId = useId()
//      const { watchList, setWatchList, selectedTicker, linkingColor } = useLinkingGroupState(cardId)
//
//      // Use watchList.list to get chart tabs
//      // Use selectedTicker to get currently selected instrument info
//      // linkingColor tells you which group this component belongs to
//    }
//    ```
//
// 2. READ-ONLY CONSUMER (e.g., OrderBook, DataChart)
//    -----------------------------------------------------------------------
//    Do NOT reach for useLinkingGroupState here. It hands back the whole slice, so a
//    write to ANY field re-renders every one of its ~40 callers — destructuring just
//    one field does not narrow the subscription. That is exactly how the option chain
//    ended up re-rendering on every unrelated linking-state write.
//
//    Read one field with useLinkingGroupField (or a domain wrapper such as
//    useLinkingGroupSelectedTicker):
//
//    ```tsx
//    export default function OrderBook() {
//      const cardId = useId()
//      const selectedTicker = useLinkingGroupField(cardId, 'selectedTicker')
//
//      useOrderBookStream({ symbol: selectedTicker?.symbol, ... })
//    }
//    ```
//
//    Read useLinkingGroupField's own doc first: `watchList`, `positionFilter` and
//    `chartOption` are normalized by useLinkingGroupState before it hands them out, so
//    those three are NOT interchangeable with a raw field read.
//
//    Setter-only? Use useLinkingGroupActions(cardId) — it exposes every writer without
//    subscribing to the slice's payload at all. Need both, for one field? Pair a
//    useLinkingGroupField read with it, the way useLinkingGroupSelectedTicker does.
//
//    High-frequency market data does NOT belong in LinkingGroupState — that atom is
//    atomWithStorage-backed, so every write also re-arms a localStorage flush. Give it
//    its own plain atom keyed by optionRowsScopeKey, the way optionRowsAtomFamily and
//    optionUnderlyingPriceAtomFamily do, and expose it as its own opt-in hook.
//
// 3. ADDING NEW STATE FIELDS TO LinkingGroupState
//    -----------------------------------------------------------------------
//    If you need to add new shared state (e.g., selectedTimeframe, orderBookDepth):
//
//    a. Add the field to LinkingGroupState interface in state/linking-group.ts:
//       ```ts
//       export interface LinkingGroupState {
//         watchList: WatchList
//         selectedTicker: LinkingTickerInfo | null
//         selectedInstruments: SelectedInstrument[]
//         selectedOptions: SelectedOption[]
//         orderBookDepth?: number  // NEW FIELD
//       }
//       ```
//
//    b. Add default value in DEFAULT_GROUP_STATE:
//       ```ts
//       export const DEFAULT_GROUP_STATE: LinkingGroupState = {
//         ...
//         orderBookDepth: 20,  // DEFAULT VALUE
//       }
//       ```
//
//    c. Add accessor in useLinkingGroupState hook:
//       ```ts
//       const setOrderBookDepth = useCallback((depth: number) => {
//         setState((prev) => ({ ...prev, orderBookDepth: depth }))
//       }, [setState])
//
//       return {
//         ...
//         orderBookDepth: state.orderBookDepth,
//         setOrderBookDepth,
//       }
//       ```
//
// 4. IMPORTANT: LinkingActionHeader
//    -----------------------------------------------------------------------
//    Every component using linking should include LinkingActionHeader with cardId:
//
//    ```tsx
//    <LinkingActionHeader cardId={cardId}>
//      <YourComponentHeader />
//    </LinkingActionHeader>
//    ```
//
//    This allows users to change the component's linking color.
//
// =============================================================================

/**
 * Primary hook for components to access their linking group's state
 * Automatically handles unlinked vs linked state routing based on cardId
 *
 * @param cardId - Unique identifier for the component card
 * @returns Group state and setters scoped to the card's linking color group
 *
 * @example
 * // In a watch-list component
 * const { watchList, setWatchList, linkingColor } = useLinkingGroupState(cardId)
 */
/**
 * Just this card's resolved linking color — a single-atom subscription, for
 * callers that don't need the rest of useLinkingGroupState's (much heavier:
 * per-page group/unlinked state + chart-option merge) surface. Prefer this
 * over useLinkingGroupState(cardId).linkingColor when that's all you need,
 * especially alongside another useLinkingGroupState call in the same
 * component — two calls would otherwise double-subscribe to the group/
 * unlinked/chart-option atoms and redo the merge useMemo for nothing.
 */
export function useLinkingColor(cardId: string): LinkingColor {
  const pageId = useContext(LinkingPageContext)
  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId))
  return linkingColors[cardId] ?? 'mint'
}

/**
 * The routing half of useLinkingGroupState: writes a card's slice to whichever store
 * owns it, without subscribing to that slice's value.
 *
 * Extracted so the narrow read hooks below can offer writers without dragging the
 * whole payload subscription along. Note this still subscribes to
 * `linkingColorsAtomFamily` — the write has to know which color it belongs to — so a
 * "setter-only" consumer re-renders when its own card is re-coloured, and nothing else.
 */
export function useLinkingGroupSetState(cardId: string) {
  const pageId = useContext(LinkingPageContext)
  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId))
  const linkingColor = linkingColors[cardId] ?? 'mint'
  const isUnlinked = linkingColor === 'unlinked'

  // Writers only — `useSetAtom` does NOT subscribe, so the setters never drag a
  // full-map re-render in on their own.
  const setGroupStates = useSetAtom(linkingGroupStateAtomFamily(pageId))
  const setUnlinkedStates = useSetAtom(unlinkedCardStateAtomFamily(pageId))

  return useCallback(
    (update: Partial<LinkingGroupState> | ((prev: LinkingGroupState) => LinkingGroupState)) => {
      if (isUnlinked) {
        setUnlinkedStates((prev) => {
          const currentState = prev[cardId] ?? DEFAULT_GROUP_STATE
          const newState =
            typeof update === 'function' ? update(currentState) : { ...currentState, ...update }
          if (newState === currentState) return prev
          return { ...prev, [cardId]: newState }
        })
      } else {
        setGroupStates((prev) => {
          const currentState = prev[linkingColor] ?? DEFAULT_GROUP_STATE
          const newState =
            typeof update === 'function' ? update(currentState) : { ...currentState, ...update }
          if (newState === currentState) return prev
          return { ...prev, [linkingColor]: newState }
        })
      }
    },
    [isUnlinked, cardId, linkingColor, setUnlinkedStates, setGroupStates]
  )
}

/**
 * Subscribe to exactly ONE field of a card's linking-group slice.
 *
 * Prefer this (or a domain wrapper below) over useLinkingGroupState for read-only
 * consumers: the full hook hands back the whole slice, so a write to ANY field
 * re-renders every one of its ~40 callers. The selector is memoized on the field key,
 * which selectAtom requires — never pass an inline selector from a call site.
 *
 * IMPORTANT — only safe for fields useLinkingGroupState passes through untouched:
 * `selectedTicker`, `selectedOptionTicker`, `selectedOptionExpiry`, `focusedOptionStrike`
 * and `optionChainOverride`.
 *
 * It is NOT a drop-in for `watchList`, `positionFilter` or `chartOption`: that hook
 * normalizes all three before handing them out (focus normalization, per-sub-field
 * defaulting, and resolution against the per-widget chartOptionAtomFamily plus a legacy
 * fallback respectively). Reading them raw here would silently change behaviour — those
 * need their own normalization-aware wrappers.
 */
type DirectLinkingGroupFieldKey = Exclude<
  keyof LinkingGroupState,
  'watchList' | 'positionFilter' | 'chartOption'
>

export function useLinkingGroupField<K extends DirectLinkingGroupFieldKey>(
  cardId: string,
  key: K
): LinkingGroupState[K] {
  const pageId = useContext(LinkingPageContext)
  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId))
  const linkingColor = linkingColors[cardId] ?? 'mint'
  const isUnlinked = linkingColor === 'unlinked'

  const fieldAtom = useMemo(() => {
    // Matches useLinkingGroupState's `{ ...DEFAULT_GROUP_STATE, ...baseState }` exactly:
    // a slice persisted before this field existed must fall back to the default, not to
    // `undefined`. Written as a lookup rather than an object spread so the selector
    // doesn't allocate on every evaluation.
    const pick = (slice: LinkingGroupState | undefined) =>
      slice && key in slice ? slice[key] : DEFAULT_GROUP_STATE[key]

    return isUnlinked
      ? selectAtom(unlinkedCardStateAtomFamily(pageId), (map) => pick(map[cardId]))
      : selectAtom(linkingGroupStateAtomFamily(pageId), (map) => pick(map[linkingColor]))
  }, [pageId, cardId, linkingColor, isUnlinked, key])

  return useAtomValue(fieldAtom)
}

/**
 * The card's PositionFilter, normalized exactly as useLinkingGroupState normalizes it,
 * without subscribing to the rest of the slice.
 *
 * The normalization is why `useLinkingGroupField(cardId, 'positionFilter')` is not a
 * substitute — that returns the raw persisted value, which may be missing sub-fields.
 * Pair with useLinkingGroupActions when a writer is needed too.
 *
 * Bonus over the full hook: the sub-field equality below means the returned object keeps
 * its identity while the filter is untouched. useLinkingGroupState rebuilds it on every
 * recompute of its state memo, so consumers using it as a `useMemo`/effect dependency
 * were being invalidated by unrelated writes.
 */
export function useLinkingGroupPositionFilter(cardId: string): PositionFilter {
  const pageId = useContext(LinkingPageContext)
  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId))
  const linkingColor = linkingColors[cardId] ?? 'mint'
  const isUnlinked = linkingColor === 'unlinked'

  const filterAtom = useMemo(() => {
    const pick = (slice: LinkingGroupState | undefined) =>
      normalizePositionFilter(slice?.positionFilter)

    return isUnlinked
      ? selectAtom(
          unlinkedCardStateAtomFamily(pageId),
          (map) => pick(map[cardId]),
          isSamePositionFilter
        )
      : selectAtom(
          linkingGroupStateAtomFamily(pageId),
          (map) => pick(map[linkingColor]),
          isSamePositionFilter
        )
  }, [pageId, cardId, linkingColor, isUnlinked])

  return useAtomValue(filterAtom)
}

/**
 * `selectedTicker` plus its setter, without subscribing to the rest of the slice.
 * Used by the option chain's per-side cells, which are mounted twice per strike —
 * the full hook there meant every linking-state write re-rendered the entire chain.
 */
export function useLinkingGroupSelectedTicker(cardId: string) {
  const selectedTicker = useLinkingGroupField(cardId, 'selectedTicker')
  const setState = useLinkingGroupSetState(cardId)

  const setSelectedTicker = useCallback(
    (ticker: LinkingTickerInfo | null) => {
      setState((prev) => {
        if (isSameTickerInfo(prev.selectedTicker, ticker)) return prev
        return { ...prev, selectedTicker: ticker }
      })
    },
    [setState]
  )

  return { selectedTicker, setSelectedTicker }
}

/**
 * Every linking-group writer that routes purely through `setState`, with NO subscription
 * to the slice's payload.
 *
 * Use this for setter-only consumers — they have no business re-rendering when some
 * unrelated field changes. (It still subscribes to the card's linking color, since a
 * write has to know which slice it belongs to; see useLinkingGroupSetState.)
 *
 * `setChartOption` is deliberately NOT here: it writes to the per-widget
 * chartOptionAtomFamily rather than to the shared slice, so it needs that atom's own
 * setter and stays in useLinkingGroupState.
 */
export function useLinkingGroupActions(cardId: string) {
  const setState = useLinkingGroupSetState(cardId)

  // WatchList accessor
  const setWatchList = useCallback(
    (update: WatchList | ((prev: WatchList) => WatchList)) => {
      setState((prev) => {
        const current = normalizeWatchListFocus(prev.watchList)
        const nextInput = typeof update === 'function' ? update(current) : update
        const next = normalizeWatchListUpdate(current, nextInput)
        if (areWatchListsEqual(next, current)) return prev
        return { ...prev, watchList: next }
      })
    },
    [setState]
  )

  // Selected Ticker accessor
  const setSelectedTicker = useCallback(
    (ticker: LinkingTickerInfo | null) => {
      setState((prev) => {
        if (isSameTickerInfo(prev.selectedTicker, ticker)) return prev
        return { ...prev, selectedTicker: ticker }
      })
    },
    [setState]
  )

  // Option ticker accessor
  const setSelectedOptionTicker = useCallback(
    (ticker: string | null) => {
      setState((prev) => {
        if (prev.selectedOptionTicker === ticker) return prev
        return { ...prev, selectedOptionTicker: ticker }
      })
    },
    [setState]
  )

  // Option expiry accessor
  const setSelectedOptionExpiry = useCallback(
    (expiry: string | null) => {
      setState((prev) => {
        if (prev.selectedOptionExpiry === expiry) return prev
        return { ...prev, selectedOptionExpiry: expiry }
      })
    },
    [setState]
  )

  // Option underlying price is NOT here — it lives in optionUnderlyingPriceAtomFamily
  // and is reached via useLinkingGroupOptionUnderlyingPrice, so its per-tick writes
  // don't invalidate this whole slice. See that atom's doc comment.

  // Position filter accessor
  const setPositionFilter = useCallback(
    (update: PositionFilter | ((prev: PositionFilter) => PositionFilter)) => {
      setState((prev) => ({
        ...prev,
        positionFilter: typeof update === 'function' ? update(prev.positionFilter) : update,
      }))
    },
    [setState]
  )

  // Option chain override accessor
  const setOptionChainOverride = useCallback(
    (override: OptionChainOverride | null) => {
      setState((prev) => {
        if (isSameOptionChainOverride(prev.optionChainOverride, override)) return prev
        return { ...prev, optionChainOverride: override }
      })
    },
    [setState]
  )

  // Focused option strike accessor (for option table auto-scroll priority)
  const setFocusedOptionStrike = useCallback(
    (params: { strike: number; expiry: string; ticker: string } | null) => {
      setState((prev) => ({
        ...prev,
        focusedOptionStrike: params
          ? { ...params, _id: (prev.focusedOptionStrike?._id ?? 0) + 1 }
          : null,
      }))
    },
    [setState]
  )

  return {
    setState,
    setWatchList,
    setSelectedTicker,
    setSelectedOptionTicker,
    setSelectedOptionExpiry,
    setPositionFilter,
    setOptionChainOverride,
    setFocusedOptionStrike,
  }
}

/**
 * Pure normalization for a card's resolved ChartOption, given the already-resolved
 * per-widget value (`chartOptionAtomFamily`) and the legacy shared value (the
 * `chartOption` field still embedded in the linking-group/unlinked slice, read only as
 * a one-time migration seed — see resolveLegacyChartOption's doc comment). Does not
 * itself read any atom, so useLinkingGroupState and useLinkingGroupChartOption below can
 * share the exact same normalization (DEFAULT_GROUP_STATE merge + per-analytics-metric
 * default merge) without their implementations drifting apart.
 */
function normalizeChartOption(
  cardChartOption: ChartOption | undefined,
  legacyChartOption: ChartOption | undefined
): ChartOption {
  const resolvedChartOption: ChartOption =
    cardChartOption ?? legacyChartOption ?? DEFAULT_GROUP_STATE.chartOption

  const chartAnalytics: ChartAnalyticsOption = {
    ...DEFAULT_CHART_ANALYTICS,
    ...(resolvedChartOption.analytics ?? {}),
    vwap: {
      ...DEFAULT_CHART_ANALYTICS.vwap,
      ...(resolvedChartOption.analytics?.vwap ?? {}),
    },
    twap: {
      ...DEFAULT_CHART_ANALYTICS.twap,
      ...(resolvedChartOption.analytics?.twap ?? {}),
    },
    deltaPercentage: {
      ...DEFAULT_CHART_ANALYTICS.deltaPercentage,
      ...(resolvedChartOption.analytics?.deltaPercentage ?? {}),
    },
    netBuyVolumeBase: {
      ...DEFAULT_CHART_ANALYTICS.netBuyVolumeBase,
      ...(resolvedChartOption.analytics?.netBuyVolumeBase ?? {}),
    },
    parkinsonVolatility: {
      ...DEFAULT_CHART_ANALYTICS.parkinsonVolatility,
      ...(resolvedChartOption.analytics?.parkinsonVolatility ?? {}),
    },
    totalLiqBase: {
      ...DEFAULT_CHART_ANALYTICS.totalLiqBase,
      ...(resolvedChartOption.analytics?.totalLiqBase ?? {}),
    },
  }

  return {
    ...DEFAULT_GROUP_STATE.chartOption,
    ...resolvedChartOption,
    analytics: chartAnalytics,
  }
}

/**
 * The card's watchList, normalized exactly as useLinkingGroupState normalizes it
 * (`normalizeWatchListFocus`), without subscribing to the rest of the slice. Shares
 * `areWatchListsEqual` so the returned reference stays stable while the watchList is
 * structurally unchanged — unrelated writes to the same color/card slice (selectedTicker,
 * positionFilter, ...) don't produce a new reference here.
 *
 * Prefer this over `useLinkingGroupState(cardId).watchList` for consumers that only need
 * the tab list (e.g. the TV chart widget, the option table) — the full hook re-renders on
 * every write to the slice.
 */
export function useLinkingGroupWatchList(cardId: string): WatchList {
  const pageId = useContext(LinkingPageContext)
  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId))
  const linkingColor = linkingColors[cardId] ?? 'mint'
  const isUnlinked = linkingColor === 'unlinked'

  const watchListAtomSelector = useMemo(() => {
    const pick = (slice: LinkingGroupState | undefined) =>
      normalizeWatchListFocus(slice?.watchList ?? DEFAULT_GROUP_STATE.watchList)

    return isUnlinked
      ? selectAtom(
          unlinkedCardStateAtomFamily(pageId),
          (map) => pick(map[cardId]),
          areWatchListsEqual
        )
      : selectAtom(
          linkingGroupStateAtomFamily(pageId),
          (map) => pick(map[linkingColor]),
          areWatchListsEqual
        )
  }, [pageId, cardId, linkingColor, isUnlinked])

  return useAtomValue(watchListAtomSelector)
}

/**
 * The card's resolved `chartOption` plus its setter, normalized exactly as
 * useLinkingGroupState normalizes it (via the shared `normalizeChartOption` helper above),
 * without subscribing to the rest of the slice.
 *
 * Subscribes to two atoms: the per-widget `chartOptionAtomFamily` (the primary source once
 * a widget has its own settings) and, reactively, the legacy `chartOption` field still
 * embedded in the group/unlinked slice — needed so a value arriving later (e.g. server
 * layout restore hydrating the slice after this widget's first render) is picked up. That
 * legacy field is only ever read, never written, once a widget has migrated to its own
 * chartOptionAtomFamily entry, so this selector stays reference-stable (and causes no
 * re-render) across every other field write in the slice.
 */
export function useLinkingGroupChartOption(cardId: string) {
  const pageId = useContext(LinkingPageContext)
  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId))
  const linkingColor = linkingColors[cardId] ?? 'mint'
  const isUnlinked = linkingColor === 'unlinked'

  const legacyChartOptionAtom = useMemo(() => {
    const pick = (slice: LinkingGroupState | undefined) => slice?.chartOption
    return isUnlinked
      ? selectAtom(unlinkedCardStateAtomFamily(pageId), (map) => pick(map[cardId]))
      : selectAtom(linkingGroupStateAtomFamily(pageId), (map) => pick(map[linkingColor]))
  }, [pageId, cardId, linkingColor, isUnlinked])
  const legacyChartOption = useAtomValue(legacyChartOptionAtom)

  const scopeKey = chartWidgetScopeKey(pageId, cardId)
  const [cardChartOption, setCardChartOption] = useAtom(chartOptionAtomFamily(scopeKey))

  const chartOption = useMemo(
    () => normalizeChartOption(cardChartOption, legacyChartOption),
    [cardChartOption, legacyChartOption]
  )

  // Same fresh-state merge semantics as useLinkingGroupState's setChartOption: resolves
  // against the atom's freshly-read value inside the functional update, not a value
  // captured at render time, so two calls firing in the same tick merge correctly.
  const setChartOption = useCallback(
    (update: ChartOption | ((prev: ChartOption) => ChartOption)) => {
      setCardChartOption((stored) => {
        const current = stored ?? legacyChartOption ?? DEFAULT_GROUP_STATE.chartOption
        const patch = typeof update === 'function' ? update(current) : update
        return mergeChartOption(current, patch)
      })
    },
    [legacyChartOption, setCardChartOption]
  )

  return { chartOption, setChartOption }
}

export function useLinkingGroupState(cardId: string) {
  const pageId = useContext(LinkingPageContext)

  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId))
  const linkingColor = linkingColors[cardId] ?? 'mint'
  const isUnlinked = linkingColor === 'unlinked'

  // Sliced reads. Subscribing to the WHOLE per-page map (as `useAtom` did) meant a
  // write to any one color re-rendered every one of this hook's ~38 direct callers —
  // plus everything downstream of use-card-account / order-form-context — even for
  // widgets on a different color or unlinked entirely. Clicking a watchlist row writes
  // one color's watchList, so that single click was re-rendering the entire dashboard
  // (position tables, option tables, orderbooks, the TV chart widget, …) at once.
  // selectAtom narrows the subscription to just this card's own slice, so unrelated
  // groups' writes are no longer observable here and stop causing renders.
  const groupState = useAtomValue(
    useMemo(
      () => selectAtom(linkingGroupStateAtomFamily(pageId), (map) => map[linkingColor]),
      [pageId, linkingColor]
    )
  )
  const unlinkedState = useAtomValue(
    useMemo(
      () => selectAtom(unlinkedCardStateAtomFamily(pageId), (map) => map[cardId]),
      [pageId, cardId]
    )
  )

  // Chart settings live in their own per-widget atom (scoped by page+cardId, not
  // linking color) — see state/widget-instances.ts's chartOptionAtomFamily. A
  // widget's chart settings are never shared across same-color siblings; only
  // reads for a widget that hasn't individually set them yet fall back to the
  // legacy shared value (resolveLegacyChartOption) as a one-time migration seed.
  const scopeKey = chartWidgetScopeKey(pageId, cardId)
  const [cardChartOption, setCardChartOption] = useAtom(chartOptionAtomFamily(scopeKey))

  // Get current state based on linking status
  // Merge with DEFAULT_GROUP_STATE to ensure all fields exist (handles migration)
  const state = useMemo(() => {
    const baseState = (isUnlinked ? unlinkedState : groupState) ?? DEFAULT_GROUP_STATE

    // baseState is already the reactively-subscribed group/unlinked slice, so its own
    // `chartOption` field IS the legacy value resolveLegacyChartOption would otherwise
    // re-fetch via store.get() on every recompute — reuse it directly instead of a
    // second (non-reactive, but here redundant) read.
    const watchList = normalizeWatchListFocus(baseState.watchList)

    return {
      ...DEFAULT_GROUP_STATE,
      ...baseState,
      watchList,
      chartOption: normalizeChartOption(cardChartOption, baseState.chartOption),
      // Ensure positionFilter is always defined with all required fields
      positionFilter: normalizePositionFilter(baseState.positionFilter),
    }
  }, [isUnlinked, unlinkedState, groupState, cardChartOption])

  // Writers live in useLinkingGroupActions so setter-only consumers can take them
  // without subscribing to this slice's payload.
  const {
    setState,
    setWatchList,
    setSelectedTicker,
    setSelectedOptionTicker,
    setSelectedOptionExpiry,
    setPositionFilter,
    setOptionChainOverride,
    setFocusedOptionStrike,
  } = useLinkingGroupActions(cardId)

  // Chart option accessor — writes to its own per-widget atom (see
  // chartOptionAtomFamily), never to the shared group/unlinked state. Resolves
  // against the atom's freshly-read value inside the functional update (not a
  // value captured from render-time `state.chartOption`), so two calls firing
  // in the same tick merge against each other correctly instead of the second
  // clobbering the first.
  const setChartOption = useCallback(
    (update: ChartOption | ((prev: ChartOption) => ChartOption)) => {
      setCardChartOption((stored) => {
        const current =
          stored ??
          resolveLegacyChartOption(pageId, cardId, linkingColor) ??
          DEFAULT_GROUP_STATE.chartOption
        const patch = typeof update === 'function' ? update(current) : update
        return mergeChartOption(current, patch)
      })
    },
    [pageId, cardId, linkingColor, setCardChartOption]
  )

  // Canonical read helpers for consumers:
  // - focusedTab is the active UI tab resolved from watchList.focusedTabId/selectedIndex.
  // - effectiveTicker is the market-data symbol priority for linked widgets:
  //   optionChainOverride (full option contract) > selectedTicker > focusedTab.
  const focusedTab = state.watchList.list[state.watchList.selectedIndex] ?? null
  const effectiveTicker = state.optionChainOverride ?? state.selectedTicker ?? focusedTab

  return {
    // Full state access
    state,
    setState,

    // Individual state accessors (convenience)
    watchList: state.watchList,
    setWatchList,
    focusedTab,
    effectiveTicker,
    selectedTicker: state.selectedTicker,
    setSelectedTicker,

    // Chart configuration
    chartOption: state.chartOption,
    setChartOption,

    // Option chain state
    selectedOptionTicker: state.selectedOptionTicker,
    setSelectedOptionTicker,
    selectedOptionExpiry: state.selectedOptionExpiry,
    setSelectedOptionExpiry,
    // Position filter
    positionFilter: state.positionFilter,
    setPositionFilter,

    // Focused option strike (for option table auto-scroll)
    focusedOptionStrike: state.focusedOptionStrike,
    setFocusedOptionStrike,

    // Option chain override (takes priority over selectedTicker/watchList in linked widgets)
    optionChainOverride: state.optionChainOverride ?? null,
    setOptionChainOverride,

    // Metadata
    linkingColor,
    isUnlinked,
    cardId,
  }
}

/**
 * Live option-chain rows are intentionally opt-in. Keeping this subscription
 * out of useLinkingGroupState prevents every linked widget from re-rendering
 * on each option-market tick when only the option chain and strike selector
 * need the rows.
 */
export function useLinkingGroupOptionRows(cardId: string) {
  const pageId = useContext(LinkingPageContext)
  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId))
  const linkingColor = linkingColors[cardId] ?? 'mint'
  const [optionRows, setOptionRows] = useAtom(
    optionRowsAtomFamily(optionRowsScopeKey(pageId, linkingColor, cardId))
  )

  return { optionRows, setOptionRows }
}

/**
 * Live underlying price for this card's option chain — opt-in for the same reason
 * as useLinkingGroupOptionRows above. This ticks on essentially every option-chain
 * WS batch, so it deliberately does NOT travel in LinkingGroupState: only the
 * handful of widgets that actually display the price should re-render on it.
 */
export function useLinkingGroupOptionUnderlyingPrice(cardId: string) {
  const pageId = useContext(LinkingPageContext)
  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId))
  const linkingColor = linkingColors[cardId] ?? 'mint'
  const [optionUnderlyingPrice, setOptionUnderlyingPrice] = useAtom(
    optionUnderlyingPriceAtomFamily(optionRowsScopeKey(pageId, linkingColor, cardId))
  )

  return { optionUnderlyingPrice, setOptionUnderlyingPrice }
}

/**
 * Hook to get state for a specific color group
 * Useful for components that need to read another group's state
 *
 * @param color - The linking color to get state for
 * @returns The group state (read-only)
 */
export function useLinkingGroupByColor(color: LinkingColor) {
  const pageId = useContext(LinkingPageContext)
  // Sliced for the same reason as useLinkingGroupState above — a reader of one color
  // must not re-render when a different color's state changes.
  const groupState = useAtomValue(
    useMemo(
      () => selectAtom(linkingGroupStateAtomFamily(pageId), (map) => map[color]),
      [pageId, color]
    )
  )
  return groupState ?? DEFAULT_GROUP_STATE
}

/**
 * Hook to migrate state when linking color changes
 * Used by linking-action-header when user changes a card's linking color
 *
 * @returns Function to handle color change with state migration
 */
export function useLinkingColorMigration(cardId: string) {
  const pageId = useContext(LinkingPageContext)

  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId))
  const currentColor = linkingColors[cardId] ?? 'mint'

  // LinkingActionHeader renders in every widget, so this hook subscribing to the full
  // per-page maps re-created the exact storm the sliced reads above fix. Nothing here
  // renders from that state — it's only read at click time inside migrateToUnlinked —
  // so take write-only handles and read the current value from the store on demand.
  const setUnlinkedStates = useSetAtom(unlinkedCardStateAtomFamily(pageId))

  const migrateToUnlinked = useCallback(() => {
    if (currentColor === 'unlinked') return

    // Copy current group state to card's isolated state
    const currentGroupState =
      store.get(linkingGroupStateAtomFamily(pageId))[currentColor] ?? DEFAULT_GROUP_STATE
    setUnlinkedStates((prev) => ({
      ...prev,
      [cardId]: { ...currentGroupState },
    }))
  }, [currentColor, pageId, cardId, setUnlinkedStates])

  const cleanupPreviousUnlinkedOptionRows = useCallback(() => {
    cleanupUnlinkedOptionRows(pageId, cardId)
  }, [cardId, pageId])

  return {
    currentColor,
    migrateToUnlinked,
    cleanupPreviousUnlinkedOptionRows,
    // When moving from unlinked to a color, the card simply inherits that group's state
    // No explicit migration needed - the hook will read from the new color group
  }
}
